import React from 'react';
import { CollapsibleRow } from '../formFields/RelationshipPickerField';

import { getComsTitle } from '../hooks/incidentComsHooks';
import { StrikeTeam } from '../hooks/strikeTeamHooks';
import { ResourceSet } from '../hooks/resourceSetHooks';
import { IncidentResourceRequirement } from '../hooks/incidentResourceRequirementHooks';
import { STATUS_LABELS } from '../Resources/constants/resourceConstants';

export type CardType =
  | 'default'
  | 'name-description'
  | 'work-assignment'
  | 'coms'
  | 'strike-team'
  | 'resource-set'
  | 'incident-resource-requirements'
  | 'incident-member'
  | 'group-member';

type SelectableEntityCardSwitchProps = {
  cardType: CardType;
  entity: any; // Entity types vary by card type
  value: string[];
  toggleRelationship: (entity: any) => void;
};

const SelectableEntityCardSwitch: React.FC<SelectableEntityCardSwitchProps> = ({
  cardType,
  entity,
  value,
  toggleRelationship,
}) => {
  switch (cardType) {
    case 'name-description':
      return (
        <NameDescriptionCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'work-assignment':
      return (
        <WorkAssignmentCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'coms':
      return (
        <ComsCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'strike-team':
      return (
        <StrikeTeamCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'resource-set':
      return (
        <ResourceSetCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'incident-resource-requirements':
      return (
        <IncidentResourceCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'incident-member':
      return (
        <IncidentMemberCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    case 'group-member':
      return (
        <GroupMemberCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
    default:
      return (
        <NameDescriptionCard
          entity={entity}
          value={value}
          toggleRelationship={toggleRelationship}
        />
      );
  }
};
export default SelectableEntityCardSwitch;

// @ts-ignore
const NameDescriptionCard = ({ entity, value, toggleRelationship }) => {
  return (
    <CollapsibleRow
      label={entity?.isFromICS ? 'Add from Library' : ''}
      selected={value.includes(entity.id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => <span className="txt--semibold">{entity.name}</span>}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Description</div>
        <div className="ms-3 mt-1">{entity.description}</div>
      </div>
    </CollapsibleRow>
  );
};

const WorkAssignmentCard = ({
  // @ts-ignore
  entity,
  // @ts-ignore
  value,
  // @ts-ignore
  toggleRelationship,
}) => {
  return (
    <CollapsibleRow
      selected={value.includes(entity.work_assignment_id)}
      onClick={() => toggleRelationship(entity)}
      header={() => (
        <span className="txt--semibold">
          {entity.work_assignment || 'Unnamed Assignment'}
        </span>
      )}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Work Assignment Details</div>
        <div className="ms-3 mt-1">
          {entity.work_assignment || 'No details'}
        </div>
        {entity.leader && (
          <>
            <div className="txt--xsml text-muted mt-2">Leader</div>
            <div className="ms-3 mt-1">{entity.leader}</div>
          </>
        )}
        {entity.status && (
          <>
            <div className="txt--xsml text-muted mt-2">Status</div>
            <div className="ms-3 mt-1">{entity.status}</div>
          </>
        )}
      </div>
    </CollapsibleRow>
  );
};

// @ts-ignore
const ComsCard = ({ entity, value, toggleRelationship }) => {
  const formattedName = getComsTitle(entity);
  return (
    <CollapsibleRow
      label={entity?.isFromICS ? 'Add from Library' : ''}
      selected={value.includes(entity.id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => <span className="txt--semibold">{formattedName}</span>}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div>{formattedName}</div>
      </div>
    </CollapsibleRow>
  );
};

const StrikeTeamCard: React.FC<{
  entity: StrikeTeam;
  value: any;
  toggleRelationship: (k: StrikeTeam) => void;
}> = ({ entity, value, toggleRelationship }) => {
  return (
    <CollapsibleRow
      selected={value.includes(entity.strike_team_id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => <span className="txt--semibold">{entity.name}</span>}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Leader</div>
        <div className="ms-3 mt-1">{entity.leader}</div>
        <div className="txt--xsml text-muted">Leader Contact #</div>
        <div className="ms-3 mt-1">{entity.leader_contact}</div>
        <div className="txt--xsml text-muted">Special Instructions</div>
        <div className="ms-3 mt-1">{entity.special_instructions}</div>
      </div>
    </CollapsibleRow>
  );
};

const IncidentResourceCard: React.FC<{
  entity: IncidentResourceRequirement;
  value: any;
  toggleRelationship: (k: any) => void;
}> = ({ entity, value, toggleRelationship }) => {
  return (
    <CollapsibleRow
      selected={value.includes(entity.incident_resource_id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => <span className="txt--semibold">{entity.name}</span>}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Description</div>
        <div className="ms-3 mt-1">{entity.description}</div>
      </div>
    </CollapsibleRow>
  );
};

const ResourceSetCard: React.FC<{
  entity: ResourceSet;
  value: any;
  toggleRelationship: (k: any) => void;
}> = ({ entity, value, toggleRelationship }) => {
  // Format the header with name - kind - (status) italicized
  const formatHeader = () => {
    const parts = [entity.name];

    if (entity.kind) {
      parts.push(entity.kind);
    }

    // Only use status from ics_resource_in_incident (current_status)
    // Do NOT use the status field from ics_resource_sets
    let statusPart = null;
    if (entity.current_status) {
      const statusLabel =
        STATUS_LABELS[entity.current_status] || entity.current_status;
      statusPart = statusLabel;
    }

    return (
      <span className="txt--semibold">
        {parts[0]}
        {parts[1] && <> - {parts[1]}</>}
        {statusPart && (
          <>
            {' '}
            - <i>({statusPart})</i>
          </>
        )}
      </span>
    );
  };

  return (
    <CollapsibleRow
      selected={value.includes(entity.resource_set_id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={formatHeader}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Description</div>
        <div className="ms-3 mt-1">{entity.description}</div>
        <div className="ms-3 mt-1">
          ({entity.kind}) | {entity.quantity} | {entity.status}
        </div>
      </div>
    </CollapsibleRow>
  );
};

const IncidentMemberCard: React.FC<{
  entity: any;
  value: any;
  toggleRelationship: (k: any) => void;
}> = ({ entity, value, toggleRelationship }) => {
  return (
    <CollapsibleRow
      selected={value.includes(entity.id ?? entity.incident_member_id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => (
        <span className="txt--semibold">
          {entity.email_address || entity.name}
        </span>
      )}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        {entity.incident_seat && (
          <>
            <div className="txt--xsml text-muted">Seat</div>
            <div className="ms-3 mt-1">{entity.incident_seat}</div>
          </>
        )}
        {entity.role && (
          <>
            <div className="txt--xsml text-muted">Role</div>
            <div className="ms-3 mt-1">{entity.role}</div>
          </>
        )}
      </div>
    </CollapsibleRow>
  );
};

type RoleAssignment = {
  name: string;
};

const GroupMemberCard: React.FC<{
  entity: any;
  value: any;
  toggleRelationship: (k: any) => void;
}> = ({ entity, value, toggleRelationship }) => {
  return (
    <CollapsibleRow
      selected={value.includes(entity.id ?? entity.incident_member_id ?? '')}
      onClick={() => toggleRelationship(entity)}
      header={() => (
        <span className="txt--semibold">
          {entity.email_address || entity.name}
        </span>
      )}
    >
      <div
        style={{
          marginTop: '1rem',
          borderTop: '1px solid var(--border-primary)',
          padding: '0.75rem 0 0.5rem 1.5rem',
        }}
      >
        <div className="txt--xsml text-muted">Role Assignments</div>
        {entity.role_assignments.map(
          (assignment: RoleAssignment, index: React.Key) => (
            <div className="ms-3 mt-1" key={index}>
              {assignment.name}
            </div>
          )
        )}
      </div>
    </CollapsibleRow>
  );
};
